import { useCallback, useEffect, useRef } from 'react';

// sessionStorage-backed pending-intent store. After a 409 state_conflict
// from billing-action, we persist { action, args, previewedAt?, createdAt }
// so that on the next `get-billing-summary` fetch the modal can re-offer
// the intent via one-tap recovery.
//
// 5-minute TTL per design decision #4 — beyond that, we don't trust the
// preview+summary pair to still be representative.

const TTL_MS = 5 * 60 * 1000;

function keyFor(userId) {
  return `billingIntent:${userId ?? 'anon'}`;
}

function read(userId) {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(keyFor(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.createdAt) return null;
    return parsed;
  } catch {
    return null;
  }
}

function write(userId, payload) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(keyFor(userId), JSON.stringify(payload));
  } catch { /* quota */ }
}

function remove(userId) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(keyFor(userId));
  } catch { /* ignore */ }
}

export function usePendingBillingIntent({ userId }) {
  // Auto-clear stale on mount.
  const mounted = useRef(false);
  useEffect(() => {
    if (mounted.current) return;
    mounted.current = true;
    const current = read(userId);
    if (current && Date.now() - current.createdAt > TTL_MS) remove(userId);
  }, [userId]);

  const get = useCallback(() => {
    const current = read(userId);
    if (!current) return null;
    if (Date.now() - current.createdAt > TTL_MS) {
      remove(userId);
      return null;
    }
    return current;
  }, [userId]);

  const set = useCallback((intent) => {
    write(userId, { ...intent, createdAt: Date.now() });
  }, [userId]);

  const clear = useCallback(() => {
    remove(userId);
  }, [userId]);

  const isStale = useCallback((now = Date.now()) => {
    const current = read(userId);
    if (!current) return true;
    return now - current.createdAt > TTL_MS;
  }, [userId]);

  return { get, set, clear, isStale };
}
